import { useEffect } from "react";
import { useOutletContext } from "react-router-dom";

export type LayoutConfig = {
  /**
   * Whether to show the Header component in the layout
   */
  header: boolean;
  /**
   * Whether to show the SideAgent chat panel in the layout
   */
  sideAgent: boolean;
  /**
   * Whether to show the header action buttons (back, talk-to-advisor, AI agent)
   * in the (compact) header. When false only the menu button remains.
   */
  headerActions: boolean;
  /**
   * When true the content area drops the layout's edge framing (e.g. the
   * right-hand padding shown when there is no side panel) for a full-bleed page
   */
  fullBleed: boolean;
};

export const DEFAULT_LAYOUT_CONFIG: LayoutConfig = {
  header: true,
  sideAgent: true,
  headerActions: true,
  fullBleed: false,
};

export type PageOutletContext = {
  /**
   * Call this when the screen's async content has finished loading.
   * Triggers the page fade-in animation.
   */
  onLoadComplete: () => void;
  /**
   * Override layout chrome visibility for the current route.
   * Merged with defaults — only pass the keys you want to change.
   * Resets to defaults on route change.
   */
  setLayoutConfig: (config: Partial<LayoutConfig>) => void;
};

/**
 * Signals to the page layout that this screen's content is ready to show.
 * Triggers the CSS fade-in animation once `loading` becomes `false`.
 *
 * Screens with async data pass their loading state:
 * ```ts
 * usePageReady({ loading });
 * ```
 *
 * Screens without async data call it bare to signal immediately:
 * ```ts
 * usePageReady();
 * ```
 */
export const usePageReady = ({
  loading = false,
}: {
  loading?: boolean;
} = {}) => {
  const { onLoadComplete } = useOutletContext<PageOutletContext>();

  useEffect(() => {
    if (!loading) {
      onLoadComplete();
    }
  }, [loading, onLoadComplete]);
};
